// ---------------------------------------------------------------------------
// Shifts, sales and the daily report.
//
// The one thing worth reading here is `useCompleteSale`. It is deliberately
// NOT retried automatically, because a retry the browser decides on its own
// could ring a sale up twice. Instead every attempt carries an idempotency key
// that the server uses to recognise a repeat, and retrying is left to the
// cashier - who can see what happened.
// ---------------------------------------------------------------------------

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/apiClient';
import { listPendingSales, listHeldOps } from '../lib/offlineDb';
import { isEffectivelyOffline, useOfflineStore } from '../stores/offlineStore';
import { queryKeys } from '../lib/queryClient';
import type { PrintPlan } from '../hooks/useSalePrinting';
import type {
  CashMovementRow,
  DailySalesReport,
  Pagination,
  PaymentDraft,
  Sale,
  SaleSummary,
  Shift,
  ShiftsOverview,
  ShiftTotals,
} from '../types';

// ---------------------------------------------------------------------------
// Shifts
// ---------------------------------------------------------------------------

export interface CurrentShiftResponse {
  shift: Shift | null;
  totals: ShiftTotals | null;
}

export function useCurrentShift() {
  return useQuery({
    queryKey: queryKeys.system.currentShift,
    queryFn: () => api.get<CurrentShiftResponse>('/shifts/current'),
    // Short, because the header must notice a shift opening in another tab.
    staleTime: 15_000,
  });
}

export function useShift(id: string | null) {
  return useQuery({
    queryKey: ['shift', id],
    queryFn: () =>
      api.get<{ shift: Shift; totals: ShiftTotals; movements: CashMovementRow[] }>(`/shifts/${id}`),
    enabled: Boolean(id),
  });
}

export function useShiftHistory(enabled = true, filters: { status?: 'OPEN' | 'CLOSED'; cashierId?: string } = {}) {
  return useQuery({
    queryKey: ['shifts', 'history', filters],
    queryFn: () => {
      const params = new URLSearchParams({ pageSize: '25' });
      if (filters.status) params.set('status', filters.status);
      if (filters.cashierId) params.set('cashierId', filters.cashierId);
      return api.get<{ shifts: Shift[]; pagination: Pagination; scope: 'own' | 'all' }>(`/shifts?${params.toString()}`);
    },
    enabled,
  });
}

/// The owner's «All users» view: every open drawer, its takings, what should
/// be in it and the deliveries it sent out still unpaid. Refreshed while the
/// screen is open, so a delivery received at the till disappears here too.
export function useShiftsOverview(enabled = true) {
  return useQuery({
    queryKey: ['shifts', 'overview'],
    queryFn: () => api.get<ShiftsOverview>('/shifts/overview'),
    enabled,
    refetchInterval: 60_000,
  });
}

/// Anything that changes the drawer invalidates the header indicator too.
function invalidateShiftState(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: queryKeys.system.currentShift });
  void queryClient.invalidateQueries({ queryKey: ['shifts'] });
  void queryClient.invalidateQueries({ queryKey: ['shift'] });
}

export function useOpenShift() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { openingCashUsd: string; openingCashLbp: string; note?: string }) =>
      api.post<{ shift: Shift; message: string }>('/shifts/open', payload),
    onSuccess: () => invalidateShiftState(queryClient),
  });
}

export function useCloseShift() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...payload }: { id: string; actualCashUsd: string; actualCashLbp: string; note?: string }) => {
      const state = useOfflineStore.getState();
      const [pending, held] = await Promise.all([listPendingSales(), listHeldOps()]);
      if (isEffectivelyOffline(state) || state.phase === 'syncing' || pending.length || held.length) {
        throw new ApiError(409, 'SHIFT_SYNC_PENDING', 'Reconnect and sync pending operations before closing the shift.');
      }
      return api.post<{
        shift: Shift;
        totals: ShiftTotals;
        /// The frozen report written at close, and whether writing it failed.
        report: { id: string } | null;
        reportError: string | null;
        /// Whether and how the till should print it, from the active design.
        reportPrint: {
          autoPrint: boolean;
          copies: number;
          designVersionId: string | null;
          config: import('../types/design').ShiftReportConfig;
        };
        message: string;
      }>(`/shifts/${id}/close`, payload);
    },
    onSuccess: () => invalidateShiftState(queryClient),
  });
}

// ---------------------------------------------------------------------------
// Sales
// ---------------------------------------------------------------------------

export interface CompleteSalePayload {
  items: Array<{
    productId: string;
    quantityMode: 'WEIGHT_KG' | 'COUNT' | 'CUSTOM_AMOUNT';
    quantity: string;
    customAmount?: string;
    note?: string | null;
  }>;
  payments: PaymentDraft[];
  changeCurrency?: 'USD' | 'LBP';
  note?: string | null;
  /// Who it was sold to. Optional for a walk-in; the server requires it
  /// whenever any part of the payment is on account. Phase 7.
  customerId?: string | null;
  /// The delivery fee, already in dollars. Added after any discount and never
  /// a sale line: it moves no stock and belongs to no category.
  deliveryFeeUsd?: string;
  /// The unpaid delivery order being paid for. The server claims it inside the
  /// sale transaction, which is what makes paying twice impossible.
  deliveryOrderId?: string;
  /// The held cart this order was restored from, marked converted by the sale.
  heldOrderId?: string;
  idempotencyKey: string;
}

export function useCompleteSale() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CompleteSalePayload) =>
      api.post<{ sale: Sale; message: string; printPlan?: PrintPlan | null }>('/sales', payload),
    // No automatic retry, on purpose - see the note at the top of this file.
    retry: false,
    onSuccess: () => {
      // A sale moves stock, fills the drawer and belongs in the history.
      void queryClient.invalidateQueries({ queryKey: ['products'] });
      void queryClient.invalidateQueries({ queryKey: ['sales'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.system.currentShift });
      void queryClient.invalidateQueries({ queryKey: queryKeys.system.dashboard });
      void queryClient.invalidateQueries({ queryKey: ['report'] });
      // A sale can settle a delivery order, consume its reservation and mark a
      // held cart converted, so every screen showing those is now stale.
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
      void queryClient.invalidateQueries({ queryKey: ['order'] });
      void queryClient.invalidateQueries({ queryKey: ['held-orders'] });
      void queryClient.invalidateQueries({ queryKey: ['reservations'] });
    },
  });
}

export interface SaleFilters {
  page?: number;
  pageSize?: number;
  search?: string;
  shiftId?: string;
  /// The trading-day window, as instants. Sent on every request rather than
  /// left off, because the screen always means a specific range - "Today" is a
  /// choice, not the absence of one. The server narrows this further for a
  /// caller without `sales.view_history`; it never widens it.
  from?: string;
  to?: string;
}

export function useSales(filters: SaleFilters = {}, enabled = true) {
  return useQuery({
    queryKey: ['sales', filters],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('page', String(filters.page ?? 1));
      params.set('pageSize', String(filters.pageSize ?? 25));
      if (filters.search) params.set('search', filters.search);
      if (filters.shiftId) params.set('shiftId', filters.shiftId);
      if (filters.from) params.set('from', filters.from);
      if (filters.to) params.set('to', filters.to);

      return api.get<{
        sales: SaleSummary[];
        pagination: Pagination;
        scope: 'own' | 'all';
        /// Whether the SERVER will honour a range beyond today. The toolbar
        /// reads this rather than the permission list, so the control and the
        /// query can never disagree about which days exist.
        history: boolean;
      }>(`/sales?${params.toString()}`);
    },
    enabled,
    staleTime: 15_000,
    // The list refreshes itself. Focus-refetch is off shop-wide and a
    // delivery account can be collected from ANOTHER window (the POS while
    // Orders sits open) - without a poll, the completed sale never appears
    // until somebody reloads, which read as "the delivery is missing from
    // Orders".
    refetchInterval: 30_000,
    placeholderData: (previous) => previous,
  });
}

/// Fetch a single sale, for the receipt modal and the refund/void dialogs.
///
/// A plain read. The old second mode - `?reprint=true`, which needed its own
/// permission and stamped the copy DUPLICATE - REPRINT - went with the
/// Orders printer icon that was its only caller; putting a receipt back on
/// paper is the modal's own Print button, recorded on the print job itself.
export function useSale(id: string | null) {
  return useQuery({
    queryKey: ['sale', id],
    queryFn: () => api.get<{ sale: Sale }>(`/sales/${id}`).then((response) => response.sale),
    enabled: Boolean(id),
    staleTime: 60_000,
  });
}



// ---------------------------------------------------------------------------
// Reports
// ---------------------------------------------------------------------------

export function useDailySalesReport(day: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['report', 'daily-sales', day ?? 'today'],
    queryFn: () =>
      api.get<DailySalesReport>(`/reports/daily-sales${day ? `?day=${day}` : ''}`),
    enabled,
    staleTime: 30_000,
  });
}
